import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cartContext } from 'contexts/cartContext';
import CartProductWrapper from '../CartProductWrapper/CartProductWrapper';
import CartProductDelete from './CartProductDelete/CartProductDelete';
import CartProductDiscount from './CartProductDiscount/CartProductDiscount';
import CartProductEdit from './CartProductEdit/CartProductEdit';
import CartProductPrice from './CartProductPrice/CartProductPrice';
import CartProductQuantity from './CartProductQuantity/CartProductQuantity';
import CartProductStock from './CartProductStock/CartProductStock';
import CustomLink from 'components/Common/CustomLink/CustomLink';
import Loading from 'components/Common/Loading/Loading';
import useLargeSizeCookie from 'hooks/useLargeSizeCookie';
import useProductInfos from 'hooks/useProductInfos';
import { getLSProductUri } from 'services/generic';
import { pushGtmEvent } from 'services/gtm/gtmEvents';
import { getProductData } from 'services/gtm/gtmPageView';
import {
  formatCartProduct,
  formatProductQuantityChange,
} from 'services/gtm/gtmUtils';
import { getBrandName } from 'services/product';
import { CartProduct as CartProductType } from 'types/Cart';
import { SelectedProductAttributes } from 'types/ProductPageV2/Products';

import styles from './CartProduct.module.scss';

function CartProduct({ product }: { product: CartProductType }): JSX.Element {
  const { updateMyBasket, deleteProduct } = useContext(cartContext);
  const { t } = useTranslation('translation');
  const { isLSSku } = useLargeSizeCookie();
  const [isLoading, setIsLoading] = useState(false);
  const {
    color,
    discountPercentage,
    infoOctipas,
    limitedQty,
    line,
    modelName,
    quantity,
    reference,
    size,
    sku,
    stock,
    totalPrice,
    totalCrossedOutPrice,
    urlOctipas,
    frontView,
    frontViewCurvy,
  } = product;
  const { productInfos } = useProductInfos(sku, reference, size);
  const formattedSize = size.replace('--', '');
  const gtmProduct = getProductData(formatCartProduct(product));
  const brand =
    getBrandName((infoOctipas ?? {}) as SelectedProductAttributes) ||
    productInfos?.brand;
  const productName = brand
    ? `${brand}® - ${productInfos?.long_designation}`
    : productInfos?.long_designation;
  const editUrl = urlOctipas + '/edit/' + line;
  const isLSProduct = isLSSku(sku);

  function handleClick() {
    pushGtmEvent({
      event: '_product_click',
      category: 'panier',
      products: [{ ...gtmProduct, list_position: line }],
    });
  }

  function handleQuantityChange(newQuantity: string): void {
    const qty = parseInt(newQuantity) - quantity;
    const formattedGtmProduct = formatProductQuantityChange(
      gtmProduct,
      qty,
      line
    );

    setIsLoading(true);
    updateMyBasket({
      catalogSku: reference,
      sku,
      sizeLabel: size,
      qty: newQuantity,
      url: !!urlOctipas ? urlOctipas : '',
      isLSProduct: isLSSku(sku),
    })
      .then(updatedCart => {
        pushGtmEvent(
          {
            event: qty > 0 ? '_add_to_cart' : '_remove_from_cart',
            category: 'panier',
            products: [formattedGtmProduct],
          },
          updatedCart
        );
      })
      .finally(() => setIsLoading(false));
  }

  function handleDelete(): void {
    setIsLoading(true);
    deleteProduct(sku, reference, size, null, null, infoOctipas, quantity)
      .then(updatedCart => {
        pushGtmEvent(
          {
            event: '_remove_from_cart',
            category: 'panier',
            products: [
              {
                ...gtmProduct,
                add_to_cart_type: undefined,
                remove_from_cart_type: 'panier',
                quantity: quantity,
                list_position: line,
              },
            ],
          },
          updatedCart
        );
      })
      .finally(() => setIsLoading(false));
  }

  function LinkToProduct({
    children,
  }: {
    children: React.ReactNode;
  }): JSX.Element {
    if (urlOctipas) {
      return (
        <CustomLink
          to={isLSProduct ? getLSProductUri(urlOctipas) : urlOctipas}
          onClick={handleClick}
        >
          {children}
        </CustomLink>
      );
    }

    return <>{children}</>;
  }

  return (
    <>
      <CartProductWrapper
        classes={{ details: styles.container }}
        image={isLSProduct ? frontViewCurvy : frontView}
        title={productName ?? ''}
        LinkComponent={LinkToProduct}
      >
        <LinkToProduct>
          <p className={styles.model}>{modelName}</p>
          <p className={styles.reference}>
            <b>
              {t('LABEL_TWO_POINTS', {
                label: t('LABEL_REF'),
              })}
            </b>
            {reference}
          </p>
        </LinkToProduct>
        <p className={styles.stock}>
          <CartProductStock stock={stock} />
        </p>
        <p>
          <b>
            {t('LABEL_TWO_POINTS', {
              label: t('LABEL_SIZE'),
            })}
          </b>
          {formattedSize}
        </p>
        <p>
          <b>
            {t('LABEL_TWO_POINTS', {
              label: t('LABEL_COLOUR'),
            })}
          </b>
          {color}
        </p>
        <p className={styles.quantity}>
          <b>
            {t('LABEL_TWO_POINTS', {
              label: t('LABEL_QUANTITY'),
            })}
          </b>
        </p>
        <CartProductQuantity
          className={styles.quantitySelector}
          quantity={quantity}
          limitedQty={limitedQty}
          onQuantityChange={handleQuantityChange}
        />
        {urlOctipas && (
          <p>
            <CartProductEdit
              url={isLSProduct ? getLSProductUri(editUrl) : editUrl}
            />
          </p>
        )}
        <div className={styles.delete}>
          <CartProductDelete modelName={modelName} onDelete={handleDelete} />
        </div>
        {+discountPercentage > 0 && (
          <CartProductDiscount
            className={styles.discount}
            discountPercentage={+discountPercentage}
          />
        )}
        <div className={styles.price}>
          <CartProductPrice
            crossedOutPrice={totalCrossedOutPrice}
            price={totalPrice}
          />
        </div>
      </CartProductWrapper>
      {isLoading && <Loading loading />}
    </>
  );
}

export default CartProduct;
